import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@/modules/common';

export function usePausableTimeout(isHover: boolean, pauseOnHover: boolean, callback: () => void) {
  const { mediaMobile } = useTheme();
  const isMobile = window.matchMedia(`(${mediaMobile})`).matches;

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const remainingRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  const [status, setStatus] = useState<'idle' | 'running' | 'paused'>('idle');

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setStatus('idle');
  }, []);

  const start = useCallback(
    (duration: number) => {
      clear();
      remainingRef.current = duration;
      startRef.current = Date.now();
      timerRef.current = setTimeout(() => {
        callback();
        setStatus('idle');
      }, duration);
      setStatus('running');
    },
    [callback, clear]
  );

  const pause = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      remainingRef.current -= Date.now() - startRef.current;
      setStatus('paused');
    }
  }, []);

  const resume = useCallback(() => {
    if (!timerRef.current && remainingRef.current > 0) {
      startRef.current = Date.now();
      timerRef.current = setTimeout(() => {
        callback();
        setStatus('idle');
      }, remainingRef.current);
      setStatus('running');
    }
  }, [callback]);

  useEffect(() => {
    if (isMobile || !pauseOnHover) {
      return;
    }

    if (status === 'running' && isHover) {
      pause();
    } else if (status === 'paused' && !isHover) {
      resume();
    }
  }, [status, isHover, pauseOnHover, isMobile, pause, resume]);

  useEffect(() => {
    return () => clear();
  }, [clear]);

  return {
    start,
    pause,
    resume,
    clear,
    status,
  };
}
